/**
 * command-operation-state.js — ONE busy/in-flight model for row 1, row 2 and
 * every contextual pane.
 *
 * Why it exists: the pre-command-bar in-flight guard was re-implemented at
 * each call site. Some surfaces used local booleans and others used
 * `isButtonBusy(btn)`, tying re-entrancy to whether a particular DOM node
 * happened to be mounted. Command-bar controls mount and unmount as row 2
 * morphs, so a DOM-anchored guard would let a second click through simply
 * because the button was rebuilt. The shell now claims these same operation
 * ids at the event boundary, covering palette, shortcut and retry routes too.
 *
 * The model is therefore keyed on an OPERATION ID, not on an element:
 *
 *     const token = beginOperation('publish', { label: 'Publishing…' });
 *     …
 *     endOperation(token, { ok: true });
 *
 * Rendering is separate and idempotent. `bindOperationControl(el, id)` registers
 * a DOM node against an id; the model paints it through the kit's
 * `setButtonBusy` and repaints on every change, so a control that mounts DURING
 * an operation comes up already busy. That is the property a morphing toolbar
 * needs and the one a per-call-site boolean cannot give.
 *
 * ── Stale tokens ──────────────────────────────────────────────────────────
 * `endOperation` with a token that is not the current one is a NO-OP. Without
 * this, a slow first attempt finishing after a faster second one had started
 * would clear the state the second attempt is relying on, and the UI would go
 * idle while work was still running. Same shape as the `_lastSaveToken` /
 * `_statusErrorToken` guards `ui/toolbar.js` already carries.
 *
 * No imports beyond the kit — this module must stay importable under jsdom
 * without dragging in `shell.js`, the same constraint `ui/components.js` holds.
 */

import { setButtonBusy } from '../components.js';

/** @type {Map<string, {token: number, label: string, startedAt: number}>} */
const _active = new Map();
/** @type {Map<string, Set<{el: HTMLElement, busyLabel: string|null, restoreAriaDisabled?: string|null}>>} */
const _controls = new Map();
/** @type {Set<Function>} */
const _subscribers = new Set();

let _nextToken = 1;

/** Monotonic, never reused. Token identity IS the staleness check. */
function mintToken() {
  return _nextToken++;
}

function notify(id) {
  const state = _active.get(id) || null;
  paint(id);
  for (const fn of [..._subscribers]) {
    try {
      fn({ id, busy: state !== null, label: state?.label ?? null });
    } catch (err) {
      // One bad subscriber must not leave other controls stuck busy.
      console.error('[command-operation-state] subscriber threw', err);
    }
  }
}

/** Repaint every control bound to `id`. Idempotent by way of setButtonBusy. */
function paint(id) {
  const bound = _controls.get(id);
  if (!bound) return;
  const state = _active.get(id) || null;
  for (const entry of bound) {
    if (!entry.el || !entry.el.isConnected) continue;
    if (state && entry.restoreAriaDisabled === undefined) {
      entry.restoreAriaDisabled = entry.el.getAttribute('aria-disabled');
    }
    setButtonBusy(entry.el, state !== null, {
      busyLabel: state ? (entry.busyLabel ?? state.label) : null
    });
    if (state) entry.el.setAttribute('aria-disabled', 'true');
    else if (entry.restoreAriaDisabled !== undefined) {
      if (entry.restoreAriaDisabled === null) entry.el.removeAttribute('aria-disabled');
      else entry.el.setAttribute('aria-disabled', entry.restoreAriaDisabled);
      delete entry.restoreAriaDisabled;
    }
  }
}

/**
 * Start an operation. Returns null when one is already running for this id —
 * that null IS the in-flight guard, so callers read as:
 *
 *     const token = beginOperation('publish', { label: 'Publishing…' });
 *     if (!token) return;
 *
 * @param {string} id
 * @param {{label?: string}} [opts]
 * @returns {number|null} token, or null if already busy
 */
export function beginOperation(id, opts = {}) {
  if (!id) return null;
  if (_active.has(id)) return null;
  const token = mintToken();
  _active.set(id, {
    token,
    label: opts.label || 'Working…',
    // Injected so this module never calls Date.now() itself — keeps it pure
    // enough to test deterministically, and matches the repo's standing rule
    // against wall-clock values in code that gates behaviour.
    startedAt: opts.startedAt ?? 0
  });
  notify(id);
  return token;
}

/**
 * Finish an operation. A stale token is a no-op — see the header.
 *
 * Takes no outcome argument on purpose. An earlier draft accepted
 * `{ ok, message }` and did nothing with it, which is the shape of an API that
 * looks like it reports failures and does not. Success and failure chrome
 * belongs to the caller (a toast, a status label, a destination card); this
 * model owns *in-flight*, and only that.
 *
 * @returns {boolean} whether this call actually ended anything
 */
export function endOperation(token) {
  if (token == null) return false;
  for (const [id, state] of _active) {
    if (state.token !== token) continue;
    _active.delete(id);
    notify(id);
    return true;
  }
  return false;
}

/** @returns {boolean} */
export function isOperationActive(id) {
  return _active.has(id);
}

/** A handed-off token is valid only while it still owns this operation id. */
export function isOperationTokenActive(id, token) {
  return token != null && _active.get(id)?.token === token;
}

/** @returns {string|null} the in-flight label, for a caller rendering its own chrome. */
export function operationLabel(id) {
  return _active.get(id)?.label ?? null;
}

/**
 * Bind a control to an operation id. Paints immediately, so a control mounted
 * mid-operation comes up busy rather than looking idle while work runs.
 *
 * @param {HTMLElement} el
 * @param {string} id
 * @param {{busyLabel?: string|null}} [opts] — null keeps the control's own label
 * @returns {() => void} unbind
 */
export function bindOperationControl(el, id, opts = {}) {
  if (!el || !id) return () => {};
  if (!_controls.has(id)) _controls.set(id, new Set());
  const entry = { el, busyLabel: opts.busyLabel === undefined ? null : opts.busyLabel };
  _controls.get(id).add(entry);
  paint(id);
  return () => {
    const set = _controls.get(id);
    if (!set) return;
    set.delete(entry);
    if (set.size === 0) _controls.delete(id);
  };
}

/**
 * Release every binding owned by a DOM subtree before that subtree is
 * replaced. Context panes intentionally remount as selection/value state
 * changes; without this release, obsolete buttons stay strongly held forever.
 *
 * @param {Node|null} root
 */
export function releaseOperationControls(root) {
  if (!root) return;
  for (const [id, set] of _controls) {
    for (const entry of [...set]) {
      if (entry.el === root || root.contains?.(entry.el)) set.delete(entry);
    }
    if (set.size === 0) _controls.delete(id);
  }
}

/**
 * @param {(change: {id: string, busy: boolean, label: string|null}) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeOperations(fn) {
  if (typeof fn !== 'function') return () => {};
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/** Test hook — drops all state so a suite starts clean. */
export function _resetOperationStateForTests() {
  _active.clear();
  _controls.clear();
  _subscribers.clear();
  _nextToken = 1;
}
